/**
 * FeedRefreshContext
 *
 * Provides global refresh state shared between Header and Feed.
 * Manages trending post priority and highlight state.
 * Manual refresh: instant scroll + seen-demotion reorder + brief spinner.
 */
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { incrementSeenBatch } from '../slices/seenHistorySlice';
import { scrollAllToTop } from '../../../utils/scrollToTop';

const FeedRefreshContext = createContext(null);

const HIGHLIGHT_DURATION_MS = 2500;

export const FeedRefreshProvider = ({ children }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const isRefreshingRef = useRef(false);
    const orderingRefreshRef = useRef(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const [priorityPostId, setPriorityPostIdState] = useState(null);
    const [priorityPostData, setPriorityPostData] = useState(null);
    const [highlightedPostId, setHighlightedPostId] = useState(null);
    const highlightTimerRef = useRef(null);
    const processedPostParamRef = useRef(null);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const postParam = searchParams.get('post');

        if (postParam && postParam !== processedPostParamRef.current && location.pathname === '/home') {
            processedPostParamRef.current = postParam;

            if (highlightTimerRef.current) {
                clearTimeout(highlightTimerRef.current);
                highlightTimerRef.current = null;
            }

            setPriorityPostIdState(postParam);
            setHighlightedPostId(postParam);

            highlightTimerRef.current = setTimeout(() => {
                setHighlightedPostId(null);
                highlightTimerRef.current = null;
            }, HIGHLIGHT_DURATION_MS);
        }
    }, [location.search, location.pathname]);

    const registerOrderingRefresh = useCallback((fn) => {
        orderingRefreshRef.current = fn;
    }, []);

    const unregisterOrderingRefresh = useCallback(() => {
        orderingRefreshRef.current = null;
    }, []);

    const refreshFeed = useCallback(() => {
        if (isRefreshingRef.current) return;

        isRefreshingRef.current = true;
        setIsRefreshing(true);

        setPriorityPostIdState(null);
        setPriorityPostData(null);

        try {
            scrollAllToTop('instant');
            dispatch(incrementSeenBatch());
            orderingRefreshRef.current?.();
        } catch (error) {
            console.error('[FeedRefresh] Error:', error);
        } finally {
            isRefreshingRef.current = false;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setIsRefreshing(false));
            });
        }
    }, [dispatch]);

    const setPriorityPost = useCallback((postId, fallbackData = null) => {
        if (highlightTimerRef.current) {
            clearTimeout(highlightTimerRef.current);
            highlightTimerRef.current = null;
        }

        setPriorityPostIdState(postId);
        setPriorityPostData(fallbackData);
        setHighlightedPostId(postId);

        if (postId) {
            scrollAllToTop('instant');
        }
    }, []);

    const clearHighlight = useCallback(() => {
        setHighlightedPostId(null);
    }, []);

    useEffect(() => {
        if (location.pathname !== '/home' && location.pathname !== '/') {
            setHighlightedPostId(null);
            setPriorityPostIdState(null);
            setPriorityPostData(null);
            processedPostParamRef.current = null;
            if (highlightTimerRef.current) {
                clearTimeout(highlightTimerRef.current);
                highlightTimerRef.current = null;
            }
        }
    }, [location.pathname]);

    return (
        <FeedRefreshContext.Provider value={{
            isRefreshing,
            refreshFeed,
            registerOrderingRefresh,
            unregisterOrderingRefresh,
            priorityPostId,
            priorityPostData,
            highlightedPostId,
            setPriorityPost,
            clearHighlight
        }}>
            {children}
        </FeedRefreshContext.Provider>
    );
};

export const useFeedRefresh = () => {
    const context = useContext(FeedRefreshContext);
    if (!context) {
        return {
            isRefreshing: false,
            refreshFeed: () => { },
            registerOrderingRefresh: () => { },
            unregisterOrderingRefresh: () => { },
            priorityPostId: null,
            priorityPostData: null,
            highlightedPostId: null,
            setPriorityPost: () => { },
            clearHighlight: () => { }
        };
    }
    return context;
};

export default FeedRefreshContext;
